import { useEffect, useRef, useState } from 'react';
import { useDialogA11y } from '../lib/useDialogA11y';
import { Sparkles, Check, X, Shield, Award, CreditCard } from 'lucide-react';
import {
  formatBillingPrice,
  loadBillingStatus,
  openBillingPortal,
  startBillingCheckout,
  type BillingInterval,
  type BillingStatus,
} from '../lib/cloud';

export interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  requiredTier: 'semipro' | 'pro';
  description?: string;
}

const SEMIPRO_FEATURES = [
  { title: 'Plejeplaner', desc: 'Flerårige planer, såbedsoverblik og sæsonplanlægning' },
  {
    title: 'Såningsgennemgang',
    desc: 'Live jordtemperatur (0-7 cm såbed) og 14-dages så-prognose',
  },
  {
    title: 'Jagt-POI på kortet',
    desc: 'Skydetårne m. skudretning, fodertønder, vildtkameraer, fældesensorer, saltsten og jordmålere',
  },
  { title: 'Læhegn-tegning', desc: 'Automatisk 6 m bredt læhegn med areal- og frøberegning' },
];

const PRO_FEATURES = [
  { title: 'Alt fra Semi-pro', desc: 'Fuld adgang til alle Semi-pro funktioner og værktøjer' },
  {
    title: 'Mine kunder (Kundestyring)',
    desc: 'Håndter kunders revirer, del planer og udarbejd professionelle rådgivningsrapporter',
  },
  {
    title: 'Biotopplaner & Kvoteoverblik',
    desc: 'Officiel pointberegning jf. Bekendtgørelsen § 14, målarter, udsætningskvoter og Faunamaster-bestilling',
  },
  {
    title: 'Landbrugsstyrelsens Markblokke',
    desc: 'Hent officielle markblokke direkte og konvertér til revirmarker med ét klik',
  },
  {
    title: 'Skovrejsning & Remiser',
    desc: 'Specialværktøj til nyplantning, skovrejsningstilskud og vildtremiser',
  },
  {
    title: 'QGIS / GeoPackage Import',
    desc: 'Importér .gpkg, Shapefiles og QGIS-planer direkte til kundens telefon',
  },
];

export default function UpgradeModal({
  isOpen,
  onClose,
  featureName,
  requiredTier,
  description,
}: UpgradeModalProps) {
  const { ref: dialogRef } = useDialogA11y({ open: isOpen, onClose });

  // Priserne kommer live fra Stripe via serveren. Der står aldrig et hardkodet beløb her.
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError('');
    setLoadError('');
    setBilling(null);
    setBillingInterval('month');
    setLoaded(false);
    setBusy(false);
    submitting.current = false;
    loadBillingStatus()
      .then((status) => {
        if (cancelled) return;
        setBilling(status);
        if (status.subscription && ['active', 'trialing'].includes(status.subscription.status)) {
          setBillingInterval(status.subscription.interval ?? 'month');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Priserne kunne ikke hentes.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, requiredTier, retry]);

  if (!isOpen) return null;

  const isPro = requiredTier === 'pro';
  const tierTitle = isPro ? 'Professionel Rådgiver' : 'Semi-pro';
  const features = isPro ? PRO_FEATURES : SEMIPRO_FEATURES;

  const prices = billing?.enabled ? (billing.plans[requiredTier] ?? {}) : {};
  const availableIntervals = (['month', 'year'] as const).filter(
    (key) => formatBillingPrice(prices[key]) !== null,
  );
  const selectedPrice = formatBillingPrice(prices[billingInterval]);
  const priceSuffix = billingInterval === 'month' ? 'md.' : 'år';
  const hasSubscription = Boolean(
    billing?.subscription &&
    !['canceled', 'incomplete_expired'].includes(billing.subscription.status),
  );
  const canChange = Boolean(
    billing?.subscription && ['active', 'trialing'].includes(billing.subscription.status),
  );
  const combinedChange = Boolean(
    hasSubscription &&
    canChange &&
    billing?.subscription?.tier !== requiredTier &&
    billing?.subscription?.interval !== billingInterval,
  );
  const canPay =
    loaded &&
    billing?.enabled &&
    !combinedChange &&
    (selectedPrice !== null || (hasSubscription && !canChange));

  const handleUpgrade = async () => {
    if (!canPay || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const url = hasSubscription
        ? await openBillingPortal(
            canChange
              ? {
                  tier: requiredTier,
                  interval: billingInterval,
                  expectedPriceId: prices[billingInterval]?.priceId,
                }
              : undefined,
          )
        : await startBillingCheckout(
            requiredTier,
            billingInterval,
            prices[billingInterval]?.priceId,
          );
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Betalingen kunne ikke startes.');
      submitting.current = false;
      setBusy(false);
    }
  };

  const buttonLabel = busy
    ? 'Åbner Stripe…'
    : !loaded
      ? 'Henter priser…'
      : hasSubscription && !canChange
        ? 'Fortsæt til Stripe · administrer betaling'
        : selectedPrice
          ? `Fortsæt til Stripe · ${selectedPrice}/${priceSuffix}`
          : 'Fortsæt til Stripe';

  return (
    <div
      ref={dialogRef}
      className="upgrade-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      onClick={onClose}
    >
      <div className="upgrade-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="upgrade-modal-close"
          onClick={onClose}
          aria-label="Luk vindue"
        >
          <X size={18} />
        </button>

        <div className="upgrade-modal-header">
          <div className="upgrade-modal-badge-row">
            <span className={`upgrade-tier-pill ${isPro ? 'pro' : 'semipro'}`}>
              {isPro ? <Award size={13} /> : <Shield size={13} />}
              {tierTitle}
              {selectedPrice ? ` · ${selectedPrice} / ${priceSuffix}` : ''}
            </span>
          </div>
          <h2 id="upgrade-modal-title" className="upgrade-modal-heading">
            Lås op for <span>{featureName}</span>
          </h2>
          {description && <p className="upgrade-modal-lead">{description}</p>}
        </div>

        <div className="upgrade-modal-body">
          <div className="upgrade-features-heading">
            <Sparkles size={16} />
            <span>Med {tierTitle} får du fuld adgang til:</span>
          </div>

          <div className="upgrade-features-grid">
            {features.map((f) => (
              <div key={f.title} className="upgrade-feature-item">
                <div className="upgrade-feature-check">
                  <Check size={14} />
                </div>
                <div className="upgrade-feature-text">
                  <strong>{f.title}</strong>
                  <span>{f.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="upgrade-checkout-box">
            {availableIntervals.length > 0 && (!hasSubscription || canChange) && (
              <div className="upgrade-interval-toggle" role="group" aria-label="Betalingsinterval">
                {availableIntervals.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={billingInterval === key ? 'active' : ''}
                    aria-pressed={billingInterval === key}
                    onClick={() => setBillingInterval(key)}
                    disabled={
                      busy ||
                      Boolean(
                        canChange &&
                        billing?.subscription?.tier !== requiredTier &&
                        billing?.subscription?.interval !== key,
                      )
                    }
                  >
                    {key === 'month' ? 'Månedligt' : 'Årligt'}
                    <small>{formatBillingPrice(prices[key])}</small>
                  </button>
                ))}
              </div>
            )}
            {selectedPrice && (!hasSubscription || canChange) && (
              <p className="upgrade-payment-summary" aria-live="polite">
                <strong>{tierTitle}</strong>
                <span>
                  {selectedPrice} inkl. moms betales én gang om
                  {billingInterval === 'year' ? ' året.' : ' måneden.'}
                </span>
                <span>Fornyes automatisk. Kan opsiges til udløbet af den betalte periode.</span>
              </p>
            )}
            {hasSubscription && (
              <p className="upgrade-checkout-note">
                Stripe viser beløbet nu og den fremtidige pris inkl. moms, før du bekræfter
                ændringen. Skift niveau og betalingsinterval hver for sig under Konto.
              </p>
            )}
            <button
              type="button"
              className="button primary upgrade-pay-btn"
              onClick={handleUpgrade}
              disabled={busy || !canPay}
              aria-busy={busy}
            >
              <CreditCard size={16} />
              <span>{buttonLabel}</span>
            </button>
            <p className="upgrade-checkout-note">
              {!loaded
                ? 'Vi henter de aktuelle priser.'
                : loadError
                  ? 'Priser og abonnement kunne ikke hentes. Prøv igen.'
                  : !billing?.enabled
                    ? 'Betaling åbner snart.'
                    : !canPay
                      ? 'Vælg et tilgængeligt betalingsinterval for at fortsætte.'
                      : 'Du bliver først opkrævet, når du bekræfter købet eller ændringen hos Stripe. Fremtidige fornyelser sker automatisk.'}
            </p>
            {loadError && (
              <>
                <p className="upgrade-checkout-error" role="alert">
                  {loadError}
                </p>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  Prøv igen
                </button>
              </>
            )}
            {error && (
              <>
                <p className="upgrade-checkout-error" role="alert">
                  {error}
                </p>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  Hent aktuelle priser
                </button>
              </>
            )}
          </div>
        </div>

        <div className="upgrade-modal-footer">
          <button type="button" className="text-button" onClick={onClose}>
            Fortsæt med nuværende visning
          </button>
        </div>
      </div>
    </div>
  );
}
